import { blockText, textBlocks, type BlockKind, type TextBlock } from './textBlocks.js';

/**
 * Zwei Stände eines Textes vergleichen (Versionen, PLAN.md §28): wie in Scrivener wählbar nach Absätzen, Sätzen, Satzteilen oder Wörtern.
 * Verglichen wird der lesbare Text (`textBlocks`: Verweise und Zeitmarken als Wortlaut, ohne Auszeichnung) – erst Absatz gegen Absatz,
 * dann in geänderten Absätzen Stück für Stück. Was wegfiel und was dazukam, steht an seiner Stelle; zusammenhängende Änderungen sind
 * nummeriert (Sprung zur nächsten).
 */

export type DiffGranularity = 'paragraph' | 'sentence' | 'phrase' | 'word';
export const DIFF_GRANULARITIES: DiffGranularity[] = ['paragraph', 'sentence', 'phrase', 'word'];

export type DiffOp = 'same' | 'added' | 'removed';

export interface DiffSpan {
  op: DiffOp;
  text: string;
  /** Nummer der Änderung (ab 0), zu der das Stück gehört; fehlt bei Unverändertem */
  change?: number;
}

export interface DiffBlock {
  kind: BlockKind;
  /** ganz weggefallen bzw. dazugekommen, unverändert oder in sich geändert */
  op: DiffOp | 'changed';
  spans: DiffSpan[];
  level?: number;
  number?: number;
}

export interface TextComparison {
  blocks: DiffBlock[];
  /** Zahl der Änderungen */
  changes: number;
}

/** Mehr Unterschiede rechnet der Vergleich nicht genau: der Rest gilt als ersetzt */
const MAX_EDITS = 1500;

/** Schritte von `a` nach `b` (Myers, kürzeste Folge), in Reihenfolge; `i` bzw. `j` ist die Stelle in `a` bzw. `b` */
function editScript(a: readonly string[], b: readonly string[]): { op: DiffOp; i: number; j: number }[] {
  // gleicher Anfang und gleiches Ende vorab
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const head = Array.from({ length: start }, (_, k) => ({ op: 'same' as const, i: k, j: k }));
  const tail = Array.from({ length: a.length - endA }, (_, k) => ({ op: 'same' as const, i: endA + k, j: endB + k }));
  const n = endA - start;
  const m = endB - start;
  const at = (k: number): string => a[start + k]!;
  const bt = (k: number): string => b[start + k]!;

  const middle: { op: DiffOp; i: number; j: number }[] = [];
  const limit = Math.min(n + m, MAX_EDITS);
  const off = limit + 1;
  const v = new Int32Array(2 * off + 1);
  v[off + 1] = 0;
  // je Schritt d der Ausschnitt [-d-1, d+1] von v vor dem Schritt
  const trace: Int32Array[] = [];
  let done = n + m === 0;
  for (let d = 0; d <= limit && !done; d++) {
    trace.push(v.slice(off - d - 1, off + d + 2));
    for (let k = -d; k <= d; k += 2) {
      let x = k === -d || (k !== d && v[off + k - 1]! < v[off + k + 1]!) ? v[off + k + 1]! : v[off + k - 1]! + 1;
      let y = x - k;
      while (x < n && y < m && at(x) === bt(y)) {
        x++;
        y++;
      }
      v[off + k] = x;
      if (x >= n && y >= m) {
        done = true;
        break;
      }
    }
  }
  if (!done) {
    // zu verschieden: alles Alte weg, alles Neue dazu
    for (let k = 0; k < n; k++) middle.push({ op: 'removed', i: start + k, j: -1 });
    for (let k = 0; k < m; k++) middle.push({ op: 'added', i: -1, j: start + k });
  } else if (n + m > 0) {
    let x = n;
    let y = m;
    for (let d = trace.length - 1; d >= 0; d--) {
      const w = trace[d]!;
      const get = (k: number): number => w[k + d + 1]!;
      const k = x - y;
      const prevK = k === -d || (k !== d && get(k - 1) < get(k + 1)) ? k + 1 : k - 1;
      const prevX = get(prevK);
      const prevY = prevX - prevK;
      while (x > prevX && y > prevY) {
        middle.push({ op: 'same', i: start + x - 1, j: start + y - 1 });
        x--;
        y--;
      }
      if (d > 0) {
        if (x === prevX) middle.push({ op: 'added', i: -1, j: start + y - 1 });
        else middle.push({ op: 'removed', i: start + x - 1, j: -1 });
      }
      x = prevX;
      y = prevY;
    }
    middle.reverse();
  }
  return [...head, ...middle, ...tail];
}

const cut = (text: string, boundary: RegExp): string[] => {
  const out: string[] = [];
  let last = 0;
  for (const m of text.matchAll(boundary)) {
    const end = m.index + m[0].length;
    if (end > last) {
      out.push(text.slice(last, end));
      last = end;
    }
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

const CLOSE = '["“”„»«’\'‘)\\]]*';
const SENTENCE = new RegExp(`[.!?…]+${CLOSE}(?:\\s+|$)|\\n`, 'gu');
const PHRASE = new RegExp(`(?:[.!?…,;:]+|\\s[–—-](?=\\s))${CLOSE}(?:\\s+|$)|\\n`, 'gu');

/** Stücke eines Absatzes je Genauigkeit; zusammen ergeben sie wieder den Absatz */
export function textPieces(text: string, by: DiffGranularity): string[] {
  if (text === '') return [];
  if (by === 'paragraph') return [text];
  if (by === 'word') return text.match(/\S+\s*|\s+/gu) ?? [text];
  return cut(text, by === 'sentence' ? SENTENCE : PHRASE);
}

/**
 * Stücke in Reihenfolge der Schritte; in jeder Änderung erst das Weggefallene, dann das Neue. Leerraum am Ende eines Stücks zählt nicht
 * als Änderung („still.“ am Absatzende gleicht „still. “ vor einem neuen Satz); Gleiches steht im neuen Wortlaut da.
 */
function spansOf(a: readonly string[], b: readonly string[]): DiffSpan[] {
  const out: DiffSpan[] = [];
  const push = (op: DiffOp, text: string): void => {
    if (text === '') return;
    const last = out.at(-1);
    if (last && last.op === op) last.text += text;
    else out.push({ op, text });
  };
  let removed = '';
  let added = '';
  const settle = (): void => {
    push('removed', removed);
    push('added', added);
    removed = '';
    added = '';
  };
  const key = (x: string): string => x.trimEnd();
  for (const s of editScript(a.map(key), b.map(key))) {
    if (s.op === 'removed') removed += a[s.i];
    else if (s.op === 'added') added += b[s.j];
    else {
      settle();
      push('same', b[s.j]!);
    }
  }
  settle();
  return out;
}

const keyOf = (b: TextBlock): string => `${b.kind}:${b.level ?? ''}:${blockText(b)}`;
const shape = (b: TextBlock): Pick<DiffBlock, 'kind' | 'level' | 'number'> => ({
  kind: b.kind,
  ...(b.level !== undefined ? { level: b.level } : {}),
  ...(b.number !== undefined ? { number: b.number } : {}),
});

/** `older` mit `newer` vergleichen (beides Markdown eines Textdokuments) */
export function compareTexts(older: string, newer: string, by: DiffGranularity = 'word'): TextComparison {
  const a = textBlocks(older);
  const b = textBlocks(newer);
  const blocks: DiffBlock[] = [];
  let gone: TextBlock[] = [];
  let come: TextBlock[] = [];
  // weggefallene und neue Absätze an einer Stelle: paarweise in sich verglichen, der Rest ganz
  const settle = (): void => {
    const pairs = by === 'paragraph' ? 0 : Math.min(gone.length, come.length);
    for (let k = 0; k < pairs; k++) {
      const spans = spansOf(textPieces(blockText(gone[k]!), by), textPieces(blockText(come[k]!), by));
      blocks.push({ ...shape(come[k]!), op: 'changed', spans });
    }
    for (const g of gone.slice(pairs)) blocks.push({ ...shape(g), op: 'removed', spans: [{ op: 'removed', text: blockText(g) }] });
    for (const c of come.slice(pairs)) blocks.push({ ...shape(c), op: 'added', spans: [{ op: 'added', text: blockText(c) }] });
    gone = [];
    come = [];
  };
  for (const s of editScript(a.map(keyOf), b.map(keyOf))) {
    if (s.op === 'removed') gone.push(a[s.i]!);
    else if (s.op === 'added') come.push(b[s.j]!);
    else {
      settle();
      blocks.push({ ...shape(b[s.j]!), op: 'same', spans: [{ op: 'same', text: blockText(b[s.j]!) }] });
    }
  }
  settle();
  // nummerieren: zusammenhängende Änderungen in einem Absatz bzw. ganze Absätze hintereinander sind eine
  let changes = 0;
  let inChange = false;
  for (const block of blocks) {
    if (block.op !== 'removed' && block.op !== 'added') inChange = false;
    for (const span of block.spans) {
      if (span.op === 'same') {
        inChange = false;
        continue;
      }
      if (!inChange) changes++;
      inChange = true;
      span.change = changes - 1;
    }
    if (block.op === 'changed') inChange = false;
  }
  return { blocks, changes };
}
